import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { Subscription } from 'rxjs';
import { CountriesResponse } from 'src/app/core/models/country';
import { OrganizationCheckResponse, OrganizationCheckStatus } from 'src/app/core/models/organization-check-response';
import { IPhoneModel } from 'src/app/core/models/RegisterStrangerRequest';
import { StrangerOrganizationInfo, StrangerPermissions } from 'src/app/core/models/stranger-permissions';
import { ProfileService } from 'src/app/core/services/profile.service';
import { RegistrationService } from 'src/app/modules/auth/services/registration.service';
import { NotificationComponent } from 'src/app/modules/shared/components/notification/notification.component';
import { FormControl } from '@angular/forms';
import { strictEmailValidator } from 'src/app/core/models/custom-validators.model';

export interface JoinOrganizationModalData {
  strangerInfo: StrangerOrganizationInfo | StrangerPermissions,
  phoneCountries: CountriesResponse[]
}

@Component({
  selector: 'app-join-organization-modal',
  templateUrl: './join-organization-modal.component.html',
  styleUrls: ['./join-organization-modal.component.scss']
})
export class JoinOrganizationModalComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  isLoading: boolean = false;
  countries: CountriesResponse[] = [];
  private subscriptions: Subscription = new Subscription();

  organizationCheckInfo: OrganizationCheckResponse | null = null;

  @ViewChild('notification') notification!: NotificationComponent;

  readonly NAME_REGEX = /^[A-Za-zА-Яа-яЁё0-9\-–—.,()"':\s'?!]+$/;
  readonly PHONE_REGEX = /^[0-9+\-()\s]+$/;
  readonly INN_REGEX = /^[A-Za-z0-9\- ]+$/;

  readonly MAX_LENGTH = 255;
  readonly MIN_LENGTH_NAME = 2;
  readonly MIN_LENGTH_ADDRESS = 5;
  readonly MAX_LENGTH_EMAIL = 64;
  readonly MIN_LENGTH_EMAIL = 5;
  readonly MAX_LENGTH_PHONE = 64;
  readonly MIN_LENGTH_PHONE = 6;
  readonly MIN_LENGTH_INN = 2;
  readonly MAX_LENGTH_INN = 20;

  registerError: string | null = null;

  searchQuery = '';
  filteredCountries: CountriesResponse[] = [];

  constructor(@Inject(MAT_DIALOG_DATA) public data: JoinOrganizationModalData,
              private dialogRef: MatDialogRef<JoinOrganizationModalComponent>,
              private formBuilder: FormBuilder,
              private profileService: ProfileService,
              private registrationService: RegistrationService,
              private translocoService: TranslocoService) {
    this.initForm();
  }

  ngOnInit(): void {
    this.loadCountries(this.data.strangerInfo.clientId);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private initForm() {
    this.form = this.formBuilder.group({
      country: ['', Validators.required],
      inn: [
        '',
        [
          Validators.required,
          Validators.pattern(this.INN_REGEX),
          Validators.minLength(this.MIN_LENGTH_INN),
          Validators.maxLength(this.MAX_LENGTH_INN)
        ]
      ],
      legalForm: [''],
      shortName: [
        '',
        [
          Validators.pattern(this.NAME_REGEX),
          Validators.minLength(this.MIN_LENGTH_NAME),
          Validators.maxLength(this.MAX_LENGTH)
        ]
      ],
      fullName: [
        '',
        [
          Validators.pattern(this.NAME_REGEX),
          Validators.minLength(this.MIN_LENGTH_NAME),
          Validators.maxLength(this.MAX_LENGTH)
        ]
      ],
      email: [
        '',
        [
          strictEmailValidator,
          Validators.minLength(this.MIN_LENGTH_EMAIL),
          Validators.maxLength(this.MAX_LENGTH_EMAIL)
        ]
      ],
      phone: [''],
      address: [
        '',
        [
          Validators.pattern(this.NAME_REGEX),
          Validators.minLength(this.MIN_LENGTH_ADDRESS),
          Validators.maxLength(this.MAX_LENGTH)
        ]
      ]
    });

    this.form.get('country')?.valueChanges.subscribe(() => {
      this.clearFieldsOnCountryChange();
      this.hideNotification();
      this.organizationCheckInfo = null;
    });

    this.form.get('inn')?.valueChanges.subscribe(() => {
      this.hideNotification();
      this.organizationCheckInfo = null;
    });
  }

  private clearFieldsOnCountryChange(): void {
    this.form.patchValue({
      inn: '',
      legalForm: '',
      shortName: '',
      fullName: '',
      email: '',
      phone: '',
      address: ''
    });
    Object.keys(this.form.controls).forEach(key => {
      if (key !== 'country') {
        this.form.get(key)?.updateValueAndValidity();
      }
    });
    this.hideNotification();
  }

  private hideNotification(): void {
    if (this.notification) {
      this.notification.hide();
    }
  }

  private loadCountries(clientId: number): void {
    this.isLoading = true;
    this.subscriptions.add(
      this.registrationService.getStrangerCoutriesByCliendId([clientId], true).subscribe({
        next: (countries) => {
          this.countries = countries;
          this.filteredCountries = countries;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.notification?.showError(
            this.translocoService.translate('errorCodes.COUNTRY_LOAD_ERROR')
          );
        }
      })
    );
  }

  isRegistrationButtonVisible(): boolean {
    const country = this.form.get('country')?.value;
    const inn = this.form.get('inn')?.value;
    return !!(country && inn && country.trim() && inn.trim());
  }

  get isCheckButtonAvailable(): boolean {
    return !this.organizationCheckInfo?.result && !this.isLoading;
  }

  get isRegistationButtonAvailable(): boolean {
    return !!this.organizationCheckInfo && this.organizationCheckInfo?.result !== OrganizationCheckStatus.ORGANIZATION_FOUND_EMPLOYEE && this.organizationCheckInfo?.result !== OrganizationCheckStatus.PERSONAL_INN_HAS_COMPANY && this.organizationCheckInfo?.result !== OrganizationCheckStatus.ORGANIZATION_APPLICATION_ALREADY_REJECTED && !this.registerError && !this.isLoading;
  }

  get isExtendedFieldsShow(): boolean {
    return  this.organizationCheckInfo?.result === OrganizationCheckStatus.ORGANIZATION_NOT_FOUND;
  }

  get phoneControl(): FormControl {
    return this.form.get('phone') as FormControl;
  }

  getErrorMessage(controlName: string): string {
    const control = this.form.get(controlName);
    if (!control || !control.errors) return '';

    if (control.hasError('required')) {
      return this.translocoService.translate('errorCodes.FIELD_REQUIRED');
    }

    if (controlName === 'email' && control.hasError('pattern')) {
      return this.translocoService.translate('errorCodes.EMAIL');
    }

    if (control.hasError('pattern')) {
      return this.translocoService.translate('errorCodes.PATTERN');
    }

    if (control.hasError('minlength')) {
      return this.translocoService.translate('errorCodes.MIN_LENGTH');
    }

    if (control.hasError('maxlength')) {
      return this.translocoService.translate('errorCodes.MAX_LENGTH');
    }

    return '';
  }

  onPhoneChange(phoneData: IPhoneModel): void {
    this.form.patchValue({
      phone: phoneData.phoneNumber
    });
  }

  onCheckOrganizationClick() {
    const coutryId = this.form.get('country')?.value;
    const inn = this.form.get('inn')?.value;

    this.isLoading = true;
    this.subscriptions.add(
      this.profileService.checkStrangerOrganization(inn, coutryId, this.data.strangerInfo.clientId)
        .subscribe({
          next: (result) => {
            this.organizationCheckInfo = result;
            this.isLoading = false;
            switch (this.organizationCheckInfo.result) {
              case OrganizationCheckStatus.ORGANIZATION_FOUND_EMPLOYEE:
              case OrganizationCheckStatus.PERSONAL_INN_HAS_COMPANY:
                this.notification?.showWarning(
                  this.translocoService.translate('profileStrangers.alreadyRegistered')
                );
                this.changeValidityForm();
                break;

              case OrganizationCheckStatus.PERSONAL_INN_HAS_NO_COMPANY:
                this.notification?.showWarning(
                  this.translocoService.translate('profileStrangers.personalInnNoCompany')
                );
                this.changeValidityForm();
                break;

              case OrganizationCheckStatus.ORGANIZATION_FOUND_NOT_EMPLOYEE:
                this.notification?.showWarning(
                  this.translocoService.translate('profileStrangers.organizationFoundNotEmployee') +
                  ' ' +
                  (this.organizationCheckInfo.orgnizationName || '') +
                  '. ' +
                  this.translocoService.translate('profileStrangers.organizationFoundNotEmployee2')
                );
                this.changeValidityForm();
                break;

              case OrganizationCheckStatus.ORGANIZATION_NOT_FOUND:
                this.notification?.showWarning(
                  this.translocoService.translate('profileStrangers.organizationNotFound')
                );
                this.changeValidityForm();
                break;

              case OrganizationCheckStatus.ORGANIZATION_APPLICATION_ALREADY_REJECTED:
                this.notification?.showWarning(
                  this.translocoService.translate('profileStrangers.organizationApplicationAlreadyRejected')
                );
                this.changeValidityForm();
                break;

              default:
                break;
            }
          },
          error: (error) => {
            console.log('Ошибка получения данных: ', error);
            this.isLoading = false;
          }
        })
    );
  }

  private changeValidityForm() {
    const extendedFiels = ['legalForm', 'fullName', 'email', 'address'];

    extendedFiels.forEach((field) => {
      const control = this.form.get(field);
      if (this.organizationCheckInfo?.result === OrganizationCheckStatus.ORGANIZATION_NOT_FOUND) {
        control?.addValidators(Validators.required);
      } else {
        control?.removeValidators(Validators.required);
        control?.setValue(null);
      }
      control?.updateValueAndValidity();
    });
  }

  onJoinToOrganizationClick() {
    this.isLoading = true;
    this.subscriptions.add(
      this.registrationService.registrationStranger(this.buildFormData())
      .subscribe(
        (result) => {
          this.isLoading = false;
          this.dialogRef.close(true);
        },
        (error) => {
          this.registerError = error.error.toString();
          this.isLoading = false;
        }
      )
    );
  }

  private buildFormData(): FormData {
    const formData = new FormData();
    let data;
    switch (this.organizationCheckInfo?.result) {
      case OrganizationCheckStatus.PERSONAL_INN_HAS_NO_COMPANY:
        data = {
          clientMnemonics: [this.data.strangerInfo.clientMnemonic],
          organization: {
            countryId: Number(this.form.get('country')?.value),
            inn: this.form.get('inn')?.value
          }
        }
        formData.append('Data', JSON.stringify({ ...data }));
        return formData;
      case OrganizationCheckStatus.ORGANIZATION_FOUND_NOT_EMPLOYEE:
        data = {
          clientMnemonics: [this.data.strangerInfo.clientMnemonic],
          organization: {
            countryId: Number(this.form.get('country')?.value),
            inn: this.form.get('inn')?.value
          }
        }
        formData.append('Data', JSON.stringify({ ...data }));
        return formData;
      case OrganizationCheckStatus.ORGANIZATION_NOT_FOUND:
        const phoneValue = this.form.get('phone')?.value;
        data = {
          clientMnemonics: [this.data.strangerInfo.clientMnemonic],
          organization: {
            countryId: Number(this.form.get('country')?.value),
            inn: this.form.get('inn')?.value,
            olf: this.form.get('legalForm')?.value,
            shortName: this.form.get('shortName')?.value,
            fullName: this.form.get('fullName')?.value,
            phone: phoneValue ? `${phoneValue.phoneNumber}` : '',
            phoneCountryCode: phoneValue ? `${phoneValue.phoneCountryCode}` : '',
            email: this.form.get('email')?.value,
            address: this.form.get('address')?.value
          }
        }
        formData.append('Data', JSON.stringify({ ...data }));
        return formData;
      default:
        return formData;
    }
  }

  closeDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  get getActiveLang(): string {
    return this.translocoService.getActiveLang();
  }

  filterCountries() {
    const query = this.searchQuery.toLowerCase().trim();
    this.filteredCountries = this.countries.filter(country => {
      const name = this.getActiveLang === 'ru'
        ? country.name.toLowerCase()
        : country.inEnglish.toLowerCase();
      return name.includes(query);
    });
  }

  onSelectOpened(opened: boolean) {
    if (opened) {
      this.searchQuery = '';
      this.filteredCountries = [...this.countries];
    }
  }

  isRegistrationFormValid(): boolean {
    if (!this.organizationCheckInfo) return false;

    const result = this.organizationCheckInfo.result;

    if (result === OrganizationCheckStatus.ORGANIZATION_FOUND_NOT_EMPLOYEE ||
        result === OrganizationCheckStatus.PERSONAL_INN_HAS_NO_COMPANY) {
      const country = this.form.get('country');
      const inn = this.form.get('inn');
      return !!(country?.valid && inn?.valid && country?.value && inn?.value);
    }

    if (result === OrganizationCheckStatus.ORGANIZATION_NOT_FOUND) {
      return this.form.valid;
    }

    return false;
  }
}
